// GastroKIQ Design System v1 — Fase 1: capa de TOKENS puros (sin JSX, sin
// efectos). Nueva identidad de marca (KIQ Blue/Purple/Pink + Deep Navy)
// preparada como base reutilizable para migrar el shell principal en una
// fase futura — esto NO reemplaza DarkUI.js ni las variables CSS existentes
// (--bg/--card/--text/... definidas en public/index.html, consumidas por
// TODA la app hoy). Es una capa nueva, aditiva, pensada para coexistir.
//
// Regla de reutilización, no duplicación:
//   - surface/text/border/status.* son ALIAS de las MISMAS variables CSS que
//     ya usa DarkUI.js (var(--bg), var(--text), var(--success-text), etc.) —
//     así el sistema nuevo hereda gratis el toggle dark/light que YA existe
//     (ThemeContext.js, data-theme en <html>) sin escribir un segundo motor
//     de tema ni duplicar ningún valor hexadecimal de superficie.
//   - brand.* SÍ son hex fijos (no var()) — igual que dark.green/red/amber/
//     etc. en DarkUI.js ya son hex fijos: son color de MARCA, no de
//     superficie, y no cambian entre modo claro/oscuro en este sistema.
//
// Punto de partida de marca (confirmado por producto):
//   KIQ Blue   #00C2FF
//   KIQ Purple #7B61FF
//   KIQ Pink   #FF4FD8
//   Deep Navy  #0A0F1E
//   White      #FFFFFF

export const brand = {
  kiqBlue: '#00C2FF',
  kiqPurple: '#7B61FF',
  kiqPink: '#FF4FD8',
  deepNavy: '#0A0F1E',
  white: '#FFFFFF',
  // Color de marca "plano" para usos que no ameritan el gradiente completo
  // (p. ej. un ícono de marca pequeño, un badge kiq de una sola tinta).
  primary: '#00C2FF',
  // Token oficial "kiq-gradient" (sección 6 del pedido). Reservado a
  // propósito para: KIQ, el CTA principal de una pantalla, estados
  // seleccionados importantes, highlights de marca — NUNCA como fondo
  // general de superficies de datos (tablas/formularios/reportes deben
  // seguir viéndose limpios, sin gradiente).
  gradient: 'linear-gradient(135deg, #00C2FF 0%, #7B61FF 50%, #FF4FD8 100%)',
};

// rgba(...) desde un hex — mismo helper que ya existe en DarkUI.js (tint),
// reimplementado aquí solo para que este módulo no dependa de DarkUI.js (y
// viceversa) — ambos pueden evolucionar/eliminarse de forma independiente
// mientras coexistan.
export function tint(hex, alpha) {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

// Superficies — alias directo de las variables CSS ya definidas en
// public/index.html (:root / :root[data-theme="light"]). Cambiar de tema
// (ThemeContext.js) repinta estos tokens automáticamente, sin JS adicional.
export const surface = {
  page: 'var(--bg)',
  card: 'var(--card)',
  elevated: 'var(--card-alt)',
  hover: 'var(--card-hover)',
  // Deep Navy es un valor de MARCA fijo (no alterna con el tema) — para
  // superficies deliberadamente oscuras/premium (branding, KIQ, nav) que
  // deben verse igual sin importar el modo claro/oscuro del resto de la app.
  dark: brand.deepNavy,
};

export const text = {
  primary: 'var(--text-strong)',
  secondary: 'var(--text)',
  muted: 'var(--gray)',
  // Texto sobre una superficie oscura de marca (surface.dark, gradient, kiq
  // buttons) — siempre blanco puro, nunca var(--text) (que en modo claro es
  // oscuro y quedaría ilegible sobre Deep Navy/gradiente).
  inverse: brand.white,
};

export const border = {
  default: 'var(--border)',
  subtle: 'var(--border-soft)',
  // Ajuste de accesibilidad documentado (sección 5 del pedido): KIQ Blue
  // puro (#00C2FF) contra el fondo claro (--bg light ≈ #FDFBF7) no alcanza
  // el contraste 3:1 que WCAG 1.4.11 pide para un indicador de foco de solo
  // borde. En vez de oscurecer el azul de marca (cambiaría el token
  // visible), el foco se implementa como borde + halo (box-shadow) en
  // Button/Input/KIQButton — el halo aporta el contraste que el borde solo
  // no garantiza, sin tocar el valor de marca.
  focus: brand.kiqBlue,
};

// Alias de los *-text ya definidos en public/index.html (ya theme-aware,
// ya usados por toda la app) — nunca un nuevo valor de estado inventado.
export const status = {
  success: 'var(--success-text)',
  warning: 'var(--warning-text)',
  danger: 'var(--danger-text)',
  info: 'var(--info-text)',
};

export const interactive = {
  // Sólido, no-gradiente a propósito (sección 6: el gradiente NO es de uso
  // indiscriminado) — el botón "primary" de uso cotidiano en pantallas de
  // datos usa KIQ Blue plano; el gradiente completo queda reservado para la
  // variante "kiq" (ver Button.js).
  primary: brand.kiqBlue,
  primaryHover: '#00A8DE', // KIQ Blue ~12% más oscuro — mismo hue, más profundidad en hover
  secondary: 'var(--card-alt)',
  disabled: 'var(--gray-faint)',
};

export const shadow = {
  sm: '0 1px 2px rgba(0,0,0,.14)',
  md: '0 8px 20px rgba(0,0,0,.16)',
  lg: '0 16px 40px rgba(0,0,0,.22)',
  // Presencia azul/morada MUY sutil (sección 12: nunca neon) — dos capas de
  // opacidad baja, nunca un solo glow saturado.
  kiq: `0 8px 24px ${tint(brand.kiqBlue, 0.16)}, 0 4px 14px ${tint(brand.kiqPurple, 0.14)}`,
};

// sm/md/lg/xl explícitos (sección 11) + variantes extra donde un componente
// concreto los necesite; "none" existe para cards de datos que deben verse
// planas (no todo necesita esquinas redondeadas).
export const radius = {
  none: '0px',
  sm: '6px',
  md: '10px',
  lg: '14px',
  xl: '18px',
  pill: '999px',
};

// Múltiplos de 4 (sección 10). Compatible con lo que ya usa la app hoy de
// forma ad-hoc (paddings de 16/20/24 en DarkCard/DarkModal, gaps de 8/9/10
// en Layout.js) — esto formaliza esa escala existente, no introduce una
// nueva que compita con ella.
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  base: '16px',
  lg: '20px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '40px',
  '4xl': '48px',
  '5xl': '64px',
};

// Familia base: Inter ya está cargada (public/index.html, Google Fonts) —
// no se agrega ninguna dependencia. `metric` usa la misma JetBrains Mono
// que dark.mono (DarkUI.js) — GastroKIQ ya la usa para valores $/%/KPI, y
// section 9 pide máxima legibilidad numérica ahí: mono + tabular-nums
// alinea dígitos en columna, útil para tablas de KPIs uno debajo de otro.
const fontFamily = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const fontFamilyMono = "'JetBrains Mono', monospace";

export const typography = {
  fontFamily,
  fontFamilyMono,
  display: { fontFamily, fontSize: '40px', fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.1 },
  h1: { fontFamily, fontSize: '30px', fontWeight: 800, letterSpacing: '-0.8px', lineHeight: 1.15 }, // = PnL.js `titulo` ya existente
  h2: { fontFamily, fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1.2 },
  h3: { fontFamily, fontSize: '18px', fontWeight: 700, letterSpacing: '-0.2px', lineHeight: 1.3 },
  title: { fontFamily, fontSize: '15px', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3 }, // = DarkModal título
  body: { fontFamily, fontSize: '13.5px', fontWeight: 400, lineHeight: 1.5 }, // = tamaño de cuerpo ya dominante (ConfirmDialog/DarkCard)
  bodySmall: { fontFamily, fontSize: '12px', fontWeight: 400, lineHeight: 1.45 },
  label: { fontFamily, fontSize: '11px', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }, // = darkLabel ya existente
  caption: { fontFamily, fontSize: '11px', fontWeight: 600, lineHeight: 1.4 },
  // Números — $/%/ratios/KPIs (sección 9: deben ser extremadamente fáciles
  // de leer). Mono + tabular-nums, tamaño = DarkKpi.value ya existente.
  metric: { fontFamily: fontFamilyMono, fontSize: '25px', fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 },
};

// Objeto único opcional para quien prefiera un solo import (`import tokens
// from '.../tokens'`) — cada sub-objeto sigue siendo importable por
// separado también.
const tokens = { brand, surface, text, border, status, interactive, shadow, radius, spacing, typography, tint };
export default tokens;
